import moment from "moment";

import { GeneratedAttachmentResponse } from "@/Common/Api/Attachments/Dto/Responses/GeneratedAttachmentResponse";
import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { ExternalEmployeeResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/ExternalEmployeeResponse";
import { InternalPatientResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/InternalPatientResponse";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { PatientBannerVertical } from "@/Common/Components/Patient/PatientBannerVertical";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper, NameHelperFormat } from "@/Common/Helpers/NameHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExternalPatientResponse } from "@/Communications/Api/Dto/Responses/ExternalPatientResponse";
import { RecipientResponse } from "@/Communications/Api/Dto/Responses/RecipientResponse";
import { CommunicationsTab_OptimizedV2 } from "@/Communications/Components/CommunicationsTab_OptimizedV2";
import { ExternalMessageFormContext } from "@/Communications/Components/ExternalCommunications/Form/ExternalMessageFormComponent";
import { DocumentImportPopupComponent } from "@/Communications/Components/ExternalCommunications/Import/DocumentImportPopupComponent";
import { ImportWizardComponent } from "@/Communications/Components/ExternalCommunications/Import/ImportWizardComponent";
import { DocumentImportInfo } from "@/Communications/Models/DocumentImportInfo";
import { MessageInfo } from "@/Communications/Models/MessageInfo";
import { IndividualDTO } from "@/Communications/Models/MessageOrTaskItem";
import { IDocumentsTabModel } from "@/Documents/Models/UI/IDocumentsTabModel";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";

interface IExternalMessageViewComponentProps {
    messageId: string;
    title: string;

    showPatientBanner?: boolean;
    showFooterControls?: boolean;
    showDeleteControl?: boolean;
    showSharedViewers?: boolean;
    disableImport?: boolean;

    onLoaded?: () => void;
    handleReplyOrForwardClick?: (messageModel: MessageInfo, context: ExternalMessageFormContext) => void;
    onDelete?: () => void;
}

class ExternalMessageViewComponentState {
    public model: MessageInfo;
    public patientInfoForBanner: LoadPatientModel;
    public sharedViewers: IndividualDTO[];

    public currentFile: AttachedObjectResponse;
    public attachmentId: string;
    public showPatientImportPopup: boolean;
    public showDocumentImportPopup: boolean;
    public documentTabModel: IDocumentsTabModel;
}

class ExternalMessageViewComponent extends BaseReactComponent<IExternalMessageViewComponentProps, ExternalMessageViewComponentState> {
    constructor(props: IExternalMessageViewComponentProps) {
        super(props);

        const initialState = new ExternalMessageViewComponentState();
        initialState.model = null;
        this.state = initialState;
    }

    public componentDidUpdate(prevProps: IExternalMessageViewComponentProps): void {
        if (this.props.messageId !== prevProps.messageId) {
            this.load(this.props.messageId);
        }
    }

    public componentDidMount(): void {
        this.load(this.props.messageId);
        documentsStore.AddChangeListener(this.changeListener, this, "patient-docs");
    }

    public componentWillUnmount(): void {
        documentsStore.RemoveChangeListener("patient-docs");
    }

    private changeListener(): void {
        this.mutateState(s => s.documentTabModel = documentsStore.DocumentsModel);
    }

    public render(): JSX.Element {
        if (!this.state.model)
            return this.renderEmpty();

        const model = this.state.model;

        let bodyCssClass = this.props.showPatientBanner ? "col-lg-8" : "col-lg-12";
        let bodyHeight = this.props.showFooterControls ? "79.6vh" : "70vh";

        return (
            <div className="workspace">

                {this.renderPatientImportPopup()}
                {this.renderDocumentImportPopup()}
                {this.renderAttachmentPreview(this.state.currentFile, this.state.attachmentId)}

                <div className="myle-page-header bottom-border">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <p className="header-font-color">
                                {this.props.title}
                            </p>
                            <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                <div className="col-xs" style={{ marginLeft: "15px", minHeight: "38px" }}>
                                    <h3 className="thin-font">
                                        {limitTextLength(model.message.Subject, 100)}
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="fxheight hide-overflow">
                    <div className={bodyCssClass} style={{ paddingLeft: "0px", paddingRight: "0px" }}>
                        <div style={{ height: bodyHeight, overflowX: "hidden", paddingTop: "30px", paddingBottom: "15px" }}>
                            {this.renderRow(
                                "FromLabel",
                                <div data-clinic={this.state.model.fromEmployee.ClientTag}>
                                    {ExternalEmployeeResponse.buildDisplayName(
                                        this.state.model.fromEmployee.FirstName,
                                        this.state.model.fromEmployee.LastName,
                                        this.state.model.fromEmployee.PrefixLookupId,
                                        this.state.model.fromEmployee.OrganizationName,
                                        this.state.model.fromEmployee.SiteName
                                    )}
                                </div>
                            )}
                            {this.renderRow("ToM2Label", <div>{this.renderRecipients(model.recipients)}</div>)}
                            {this.renderRow("AttachPatientLabel", this.renderPatient(model.patient, model.internalPatient))}
                            {this.renderRow("SubjectLabel", <div>{model.message.Subject}</div>)}
                            {this.renderRow(
                                "DateLabel",
                                <div className="tbc" style={{ paddingRight: "15px" }}>
                                    <span>{model.message.SendDate ? moment.utc(model.message.SendDate).local().format("YYYY-MM-DD HH:mm:ss") : ""}</span>
                                </div>
                            )}
                            {this.renderRow(
                                "BodyLabel",
                                <div>
                                    {JSXHelper.BreakStringIntoParagraphs(model.message.Body)}
                                </div>
                            )}
                            {(!isArrayNullOrUndefinedOrEmpty(model.attachments) || !!model.generatedAttachment) &&
                                this.renderRow("AttachmentsLabel", this.renderAttachment(this.state.model))
                            }

                            {this.props.showSharedViewers && this.renderSharedInboxDetails(this.state.sharedViewers)}
                        </div>
                    </div>

                    {this.props.showPatientBanner
                        ? this.renderPatientBanner(this.state.patientInfoForBanner, { height: bodyHeight })
                        : null
                    }
                </div>

                {this.props.showFooterControls && (
                    <div className={CommunicationsTab_OptimizedV2.footerCSS}>
                        <div className="row">
                            {this.renderLeftSideControls()}
                            {this.renderRightSideControls()}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private renderAttachmentPreview(attachedObj?: AttachedObjectResponse, blobAttachmentId?: string): JSX.Element {
        if (!attachedObj && !blobAttachmentId)
            return null;

        this.mutateState(
            s => {
                s.currentFile = null;
                s.attachmentId = null;
            },
            () => {
                if (attachedObj)
                    ExportableFileHelper.fromAttachedObject(attachedObj).openInNewWindow();

                if (blobAttachmentId)
                    MyleHelper.OpenNewWindow(PrintHelper.GetAttachmentPreviewPath(blobAttachmentId));
            }
        );
    }

    private renderAttachment(model: MessageInfo): JSX.Element {
        return (
            <div>
                {model.attachments.map((object, idx) => (
                    <div key={object.Id + "_" + idx}>
                        <a onClick={() => this.mutateState(s => s.currentFile = object)} target="_blank">{AttachedObjectResponse.getDisplayName(object)}</a>
                    </div>
                ))}

                {!model.attachments.any() && this.renderBlobAttachment(model.generatedAttachment)}
            </div>
        );
    }

    private renderBlobAttachment(generatedAttachment: GeneratedAttachmentResponse): JSX.Element {
        if (!generatedAttachment)
            return null;

        let showImportDocumentOption = cacheStore.ClinicSettings.EnableXComPatientImport
            && this.state.model.internalPatient
            && !generatedAttachment.isImported
            && DocumentImportPopupComponent.hasImportDocumentPermission
            && !this.props.disableImport;

        return (
            <div>
                <div>
                    <a onClick={() => this.mutateState(s => s.attachmentId = generatedAttachment.AttachmentId)}>
                        {generatedAttachment.getDisplayName(this.state.model.internalPatient || this.state.model.patient)}
                    </a>
                </div>
                <div>
                    {showImportDocumentOption &&
                        <a onClick={() => this.handleImportDocumentClick()}>{cacheStore.GetString("ImportDocumentLabel")}</a>
                    }
                </div>
            </div>
        );
    }

    private renderEmpty(): JSX.Element {
        return (
            <div className="workspace">
                <div className="myle-page-header bottom-border">
                    <div className="row">
                        <div className="col-xs-12">
                            <p className="header-font-color">
                                {this.props.title}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="fxheight hide-overflow" style={{ minHeight: "90vh" }}>
                    <div className="col-lg-12">
                        <div className="myle-doc-tb main-content scrollable myle-form documents" />
                    </div>
                </div>
            </div>
        );
    }

    private renderRow(label: string, field: JSX.Element): JSX.Element {
        return (
            <div className="row" style={{ marginBottom: "15px" }}>
                <div className="col-lg-2 col-md-2 col-sm-2 text-right">
                    <label className="form-label">{cacheStore.GetString(label)}</label>
                </div>
                <div className="col-lg-8 col-md-8 col-sm-8 form-field-answer">
                    {field}
                </div>
            </div>
        );
    }

    public renderRecipients(recipients: RecipientResponse[]): JSX.Element[] {
        if (!recipients || recipients.length === 0)
            return [];

        const render: JSX.Element[] = [];
        recipients.forEach((recipient, idx) => {
            let employee = recipient.Employee;
            render.push(
                <div key={`toClinicians__${idx}`} data-clinic={employee.ClientTag}>
                    {ExternalEmployeeResponse.buildDisplayName(
                        employee.FirstName,
                        employee.LastName,
                        employee.PrefixLookupId,
                        employee.OrganizationName,
                        employee.SiteName
                    )},
                </div>
            );
        });

        return render;
    }

    private renderPatient(patient: ExternalPatientResponse, internalPatient: InternalPatientResponse): JSX.Element {
        if (!patient && !internalPatient)
            return null;

        if (internalPatient)
            return this.renderLinkedPatient(internalPatient);

        const showImportPatient = cacheStore.ClinicSettings.EnableXComPatientImport
            && patient.TransferablePatientInfo
            && !this.props.disableImport;

        return (
            <div key={`patient__${patient.Id}`}>
                <p>{NameHelper.formatFullNameForHasName(patient, NameHelperFormat.FirstNameFirst)}</p>
                {showImportPatient &&
                    <a onClick={() => this.mutateState(s => s.showPatientImportPopup = true)}>{cacheStore.GetString("ImportPatientLabel")}</a>
                }
            </div>
        );
    }

    public renderLinkedPatient(patient: InternalPatientResponse): JSX.Element {
        if (!patient)
            return null;

        return (
            <HyperLinkControl
                key={`Patient__${patient.ContactId}`}
                onChange={() => patientStore.setActivePatientUI(patient.ContactId)}
                text={NameHelper.formatFullNameForHasName(patient, NameHelperFormat.FirstNameFirst)}
                className="imported-patient-link"
            />
        );
    }

    private renderPatientBanner(patientResponse: LoadPatientModel, style?: React.CSSProperties): JSX.Element {
        if (!patientResponse)
            return null;

        const patientBannerDefault = {
            Model: null,
            PatientDropdownVisible: false,
            Warnings: []
        };

        const patient = Patient.FromResponseData(patientResponse);
        let patientBannerModel: IPatientBannerModel = Immutable.fromJS(patientBannerDefault);
        patientBannerModel = patientBannerModel.set("Model", patient);

        return (
            <div className="left-border col-lg-4 patient-view-panel-right patient-banner-form-wrapper">
                <div className="myle-form scrollable" style={style}>
                    <PatientBannerVertical
                        model={patientBannerModel}
                    />
                </div>
            </div>
        );
    }

    private renderLeftSideControls(): JSX.Element {
        if (!this.props.showDeleteControl)
            return null;

        return (
            <div className="col-lg-6 col-md-6 col-sm-6">
                <ButtonControl
                    className="btn btn-danger push-right-5"
                    onClick={() => this.handleOnDelete()}
                    label={cacheStore.GetString("DeleteLabel")}
                />
            </div>
        );
    }

    private renderRightSideControls(): JSX.Element {
        let showReplyButton = this.state.model.fromEmployee.EmployeeId != sessionStore.getCurrentEmployee().ContactFacetId;

        return (
            <div className="col-lg-6 col-md-6 col-sm-6 flex-display flex-end">
                <ButtonGroup>
                    <ButtonControl
                        variant="brand"
                        onClick={() => this.handleForwardClick()}
                        label={cacheStore.GetString("ForwardLabel")}
                    />
                    {showReplyButton && (
                        <ButtonControl
                            variant="brand"
                            onClick={() => this.handleReplyClick()}
                            label={cacheStore.GetString("ReplyLabel")}
                        />
                    )}
                </ButtonGroup>
            </div>
        );
    }

    private renderSharedInboxDetails(sharedViewers: IndividualDTO[]): JSX.Element {
        if (isArrayNullOrUndefinedOrEmpty(sharedViewers))
            return null;

        return (
            <div className="row" style={{ marginTop: "5%" }}>
                <div className="col-lg-6 col-md-8 text-center xcom-shared-with">
                    <label style={{ fontStyle: "italic" }}>{cacheStore.GetString("SharedExternalCommLabel")}</label>
                    {sharedViewers.map(viewer => (
                        <p key={viewer.Id}>
                            <strong>{`${viewer.LastName}, ${viewer.FirstName}`}</strong>
                        </p>
                    ))}
                </div>
            </div>
        );
    }

    private renderPatientImportPopup(): JSX.Element {
        if (!this.state.showPatientImportPopup)
            return null;

        return (
            <ImportWizardComponent
                componentId="ImportWizardComponent"
                patient={ExternalPatientResponse.fromResponse(this.state.model.patient)}
                generatedAttachment={this.state.model.generatedAttachment}
                responsibleProviderContactFacetIds={this.state.model.recipients?.map(x => x.Employee.EmployeeId)}
                documentDate={this.state.model.recipients?.firstOrDefault()?.ReceivedDate}
                documentSource={ExternalEmployeeResponse.buildDisplayClinicName(this.state.model.fromEmployee.OrganizationName, this.state.model.fromEmployee.SiteName)}

                onClose={() => this.mutateState(s => s.showPatientImportPopup = false)}
                onImportSuccessful={() => this.mutateState(s => s.showPatientImportPopup = false, () => this.load(this.props.messageId))}
                onErrorOkClick={() => this.mutateState(s => s.showPatientImportPopup = false, () => this.load(this.props.messageId))}
            />
        );
    }

    private renderDocumentImportPopup(): JSX.Element {
        if (!this.state.showDocumentImportPopup || !this.state.documentTabModel || !DocumentImportPopupComponent.hasImportDocumentPermission)
            return null;

        return (
            <DocumentImportPopupComponent
                generatedAttachment={this.state.model.generatedAttachment}
                siteId={cacheStore.GetCurrentEmployee().ActiveSiteId}
                patientContactId={this.state.model.internalPatient.ContactId}
                responsibleClinicianContactFacetIds={this.state.model.recipients?.map(x => x.Employee.EmployeeId)}
                documentDate={this.state.model.recipients?.firstOrDefault()?.ReceivedDate}
                source={ExternalEmployeeResponse.buildDisplayClinicName(this.state.model.fromEmployee.OrganizationName, this.state.model.fromEmployee.SiteName)}
                cancelLabel="CancelLabel"
                onClose={() => this.mutateState(s => s.showDocumentImportPopup = false)}
                onImport={document => this.handleImportDocument(document)}
                onImportPlusApprove={document => this.handleImportDocument(document, true)}
            />
        );
    }

    private async handleImportDocument(documentInfo: DocumentImportInfo, shouldApprove: boolean = false): Promise<void> {
        try {
            await this.importDocument(documentInfo, shouldApprove);
            this.mutateState(s => s.showDocumentImportPopup = false, () => this.load(this.props.messageId));
        }
        catch (error) {
            this.handleError(error);
            this.mutateState(s => s.showDocumentImportPopup = false);
        }
    }

    private handleReplyClick(): void {
        if (this.props.handleReplyOrForwardClick)
            this.props.handleReplyOrForwardClick(this.state.model, ExternalMessageFormContext.Reply);
    }

    private handleForwardClick(): void {
        this.props.handleReplyOrForwardClick?.(this.state.model, ExternalMessageFormContext.Forward);
    }

    private async handleOnDelete(): Promise<void> {
        const response = await Client.postAsync<null>(
            ServerOperations.Server.Myle_ExternalCommunication_DeleteInboxMessages,
            { MessageIds: [this.props.messageId] }
        );
        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return;
        }

        this.props.onDelete?.();
    }

    private handleImportDocumentClick(): void {
        if (!this.state.model.internalPatient)
            return;

        let selectedPatient = InternalPatientResponse.toPerson(this.state.model.internalPatient);

        documentsStore.InitializeNewDocument(selectedPatient, this.state.model.generatedAttachment);
        documentsStore.EmitChangeToListeners();

        this.mutateState(s => s.showDocumentImportPopup = true);
    }

    private load(id: string): void {
        if (!id)
            return;

        let requestData = {
            ExternalMessageId: id,
            ShouldLoadPatientInfoForBanner: this.props.showPatientBanner,
            ShouldLoadSharedViewers: this.props.showSharedViewers
        };

        Client.Postv2(
            ServerOperations.Server.Myle_ExternalCommunication_LoadMessage,
            requestData,
            response => {
                this.mutateState(
                    s => {
                        s.model = MessageInfo.fromResponse(response.MessageInfo);
                        s.patientInfoForBanner = response.PatientInfoForBanner;
                        s.sharedViewers = response.SharedViewers;
                    },
                    () => this.props.onLoaded && this.props.onLoaded()
                );
            }
        );
    }

    private importDocument(documentInfo: DocumentImportInfo, shouldApprove: boolean = false): Promise<void> {
        let requestData = {
            ResponsibleProviderContactIds: documentInfo.responsibleClinicianContactIds,
            Source: documentInfo.source,
            Description: documentInfo.description,
            Note: documentInfo.note,
            DocumentDate: documentInfo.documentDate,
            IsUrgent: false,
            ClassLookupId: documentInfo.classLookupId,
            SubclassLookupId: documentInfo.subClassLookupId,
            PatientContactId: documentInfo.patientContactId,
            SiteId: documentInfo.siteId,
            GeneratedAttachment: documentInfo.generatedAttachment,
            ShouldApproveDocument: shouldApprove
        };

        return new Promise((resolve, reject) => {
            Client.Postv2(
                ServerOperations.Server.Myle_ExternalCommunication_ImportDocument,
                requestData,
                () => resolve(),
                errorMsg => reject(errorMsg)
            );
        });
    }

    private handleError(errors: ErrorMessage[]): void {
        if (isArrayNullOrUndefinedOrEmpty(errors))
            return;

        sessionStore.showPopup(errors);
    }
}

export { ExternalMessageViewComponent };
